import classNames from 'classnames';
import {
    ComponentProps,
    useCallback,
    useEffect,
    useRef,
    useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import Button from 'shared/Button';
import { NavigationSite } from 'types/Navigation';
import { PaginatedList } from 'types/PaginatedList';
import { useGet } from 'utils/hooks/useAPI';
import { useSiteSelectorContext } from '../SiteSelectorContext';
import { SiteListItem } from './SiteListItem';
import { SiteSelectorNotice } from './SiteSelectorNotice';
import { Spinner } from 'shared/Spinner';

const NAVIGATION_SITES_API = '/helios-api/navigation/sites';

interface SiteListProps extends ComponentProps<'div'> {
    initialSites?: PaginatedList<NavigationSite>;
    pills?: boolean;
}

export const SiteList = ({
    className,
    initialSites,
    pills,
    ...props
}: SiteListProps) => {
    const { t } = useTranslation('navigation', {
        keyPrefix: 'site_selector.lists.site_list',
    });

    const { urlSearchParams } = useSiteSelectorContext();

    const [sites, setSites] = useState(
        new Map(
            (initialSites?.results ?? []).map((result) => [
                result.site_id,
                result,
            ])
        )
    );

    const initialUrl = initialSites
        ? ''
        : `${NAVIGATION_SITES_API}?${new URLSearchParams([
              ['page', '1'],
              ...urlSearchParams,
          ])}`;

    const previousInitialUrl = useRef(initialUrl);

    const [nextPageUrl, setNextPageUrl] = useState(initialUrl);

    const [{ data, error, loading }] = useGet<PaginatedList<NavigationSite>>(
        nextPageUrl,
        !nextPageUrl
    );

    const next = !error && data ? data?.links?.next : initialSites?.links?.next;

    const onClickLoadMore = useCallback(() => {
        if (!loading && next) {
            setNextPageUrl(next);
        }
    }, [loading, next]);

    useEffect(() => {
        if (data?.results) {
            setSites((currentSites) => {
                const nextSites = new Map(currentSites);
                data.results.forEach((result) =>
                    nextSites.set(result.site_id, result)
                );
                return nextSites;
            });
        }
    }, [data]);

    useEffect(() => {
        if (initialUrl !== previousInitialUrl.current) {
            const hasInitialData = !initialUrl;
            if (!hasInitialData) {
                setSites(new Map());
            }

            setNextPageUrl(initialUrl);
        }

        previousInitialUrl.current = initialUrl;
    }, [initialUrl]);

    if (!loading && error)
        return <SiteSelectorNotice message="service-error" />;

    if (!loading && !sites.size)
        return <SiteSelectorNotice message="no-sites" />;

    if (loading && !error && !sites.size)
        return (
            <div className="w-full h-full flex flex-col items-center justify-start">
                <Spinner />
            </div>
        );

    return (
        <div
            className={classNames('flex flex-col gap-2', className)}
            {...props}
        >
            {sites.size > 0 ? (
                <ul className="flex flex-col">
                    {[...sites.values()].map((site) => (
                        <li key={site.site_id}>
                            <SiteListItem site={site} />
                        </li>
                    ))}
                </ul>
            ) : null}

            {!error && next && sites.size > 0 ? (
                <Button
                    loading={loading}
                    size="xs"
                    type="button"
                    onClick={onClickLoadMore}
                >
                    {t('more', {
                        count:
                            ((data ?? initialSites)?.count ?? 0) - sites.size,
                    })}
                </Button>
            ) : null}
        </div>
    );
};
